import { useState } from "react";
import { Download, Share, Plus, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useInstallPrompt } from "@/hooks/use-install-prompt";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

/**
 * Install prompt. Two surfaces:
 *
 * - Native (`beforeinstallprompt` — Chrome/Edge/Android): a bottom banner that
 *   triggers the browser's own install flow.
 * - iOS Safari: never fires `beforeinstallprompt`, so we show a modal with
 *   manual "Add to Home Screen" instructions instead.
 *
 * Shown at most once per device (localStorage flag) and only after a short
 * delay so it doesn't compete with first paint.
 */
export function InstallPrompt() {
  const { t } = useTranslation();
  const { canInstall, platform, promptInstall, dismiss } = useInstallPrompt();
  const [installing, setInstalling] = useState(false);

  if (!canInstall) return null;

  if (platform === "ios") {
    return (
      <Dialog open onOpenChange={(open) => { if (!open) dismiss(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("installApp.iosTitle")}</DialogTitle>
            <DialogDescription>{t("installApp.iosDescription")}</DialogDescription>
          </DialogHeader>

          <ol className="mt-2 space-y-4">
            <li className="flex items-start gap-3">
              <Step number={1} />
              <div className="flex flex-1 items-center gap-2 pt-0.5">
                <Share className="h-5 w-5 shrink-0 text-[#1B365D]" />
                <span className="text-sm text-muted-foreground">
                  {t("installApp.iosStep1")}
                </span>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <Step number={2} />
              <div className="flex flex-1 items-center gap-2 pt-0.5">
                <Plus className="h-5 w-5 shrink-0 text-[#1B365D]" />
                <span className="text-sm text-muted-foreground">
                  {t("installApp.iosStep2")}
                </span>
              </div>
            </li>
          </ol>

          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={dismiss}>
              {t("installApp.notNow")}
            </Button>
            <Button size="sm" onClick={dismiss}>
              {t("installApp.iosGotIt")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const handleInstall = async () => {
    setInstalling(true);
    try {
      await promptInstall();
    } finally {
      setInstalling(false);
    }
  };

  return (
    <div
      className="fixed bottom-6 end-6 z-50 w-[calc(100vw-3rem)] max-w-sm animate-in slide-in-from-bottom-5 duration-300"
      role="dialog"
      aria-label={t("installApp.title")}
    >
      <div className="relative flex items-center gap-3 rounded-2xl border border-white/10 bg-[#1B365D] p-4 text-white shadow-2xl">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/15">
          <Download className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">
            {t("installApp.title")}
          </p>
          <p className="mt-0.5 line-clamp-2 text-xs text-white/80">
            {t("installApp.description")}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={handleInstall}
            disabled={installing}
            className="rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-[#1B365D] transition hover:bg-white/90 disabled:opacity-60"
          >
            {installing ? "…" : t("installApp.install")}
          </button>
          <button
            type="button"
            onClick={dismiss}
            aria-label={t("installApp.notNow")}
            className="rounded-lg p-1.5 text-white/70 transition hover:bg-white/10 hover:text-white"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

/** Numbered step badge used in the iOS instructions. */
function Step({ number }: { number: number }) {
  return (
    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#1B365D] text-xs font-semibold text-white">
      {number}
    </span>
  );
}
